import GroupSelection from 'components/General/GroupSelection';
import SwitchButton from 'components/Switch/SwitchButton';
import { Button, getColor, Icon, LoaderInline, styled } from 'imaginarity-react-ui';
import _ from 'lodash';
import * as React from 'react';
import { getTranslated } from 'services/Helpers/TranslationHelpers';
import { useImgI18N } from 'services/ImgI18N';
import { getRidOfHtml } from './AscsFiles/AscsHelpers';
import AscsTreeView, { AscsCheckedSelection, AscsPreselectedPath } from './AscsFiles/AscsTreeView';
import { AscsProps } from './AscsFiles/AscsTypes';
import { useAscs } from './AscsFiles/useAscs';

export type { AscsProps as ArticleSectionColumnSelectionProps } from './AscsFiles/AscsTypes';

const Container = styled.div`
        padding: 5px;
        border: 1px solid ${p => getColor(p, "@middleLightGrey")};
        margin: 10px 0;
        box-shadow: 0 6px 8px -6px #000;
        grid-column-start: 1;
        grid-column-end: 3;
        max-height: 50vh;
        overflow-y: auto;
`;
const InfoRow = styled.div`
        display: grid;
        grid-template-columns: 1fr max-content;
        align-items: center;
        row-gap: 8px;   
`;
const SBContainer = styled.div`
        display: flex;
        align-items: center;            
        column-gap: 5px;
        background: ${p => getColor(p, "@veryLightGrey")};
`;
const ErrorInfo = styled.div<{ error: boolean; warn?: boolean }>`
        color: ${p => getColor(p, p.warn ? "@accentRed" : p.error ? "@accentRed" : "@accentGreen")};
        background: ${p => getColor(p, "@veryLightGrey")};
        height: 40px;
        padding-left: 10px;
        align-items: center;
        display: flex;
        font-size: 13px;
        line-height: 15px;

        svg{
            stroke: ${p => getColor(p, p.warn ? "@accentRed" : p.error ? "@accentRed" : "@accentGreen")};
        }
`;

const ArticleSectionColumnSelection: React.FC<AscsProps> = (props) => {
    const { t } = useImgI18N('wiki');
    const {
        articles, selectedGroupid,
        contentLanguage, categories, userGroups,
        currentArticle, currentCategory, currentSubCategory, currentSectionId, currentColumnId,
        showSubcats, loadSubcatArticles, loadSubcatWithSections, showArticle,
        sectionColumnSelected,
        onChangeGroup,
        articleDetailsCache, loadingArticleIds, loadingSubcatIds,
    } = useAscs(props);

    const [pendingSelection, setPendingSelection] = React.useState<AscsCheckedSelection | undefined>();
    const [deleteMode, setDeleteMode] = React.useState(false);

    // When selectValue() loads a pre-existing value asynchronously, sync pendingSelection.
    // We intentionally do NOT clear pendingSelection when currentArticle is undefined,
    // because async side effects of tree navigation (showSubcats etc.) set currentArticle=undefined
    // and would wipe a user-initiated category/subcategory selection.
    // Explicit deselection is handled exclusively by the onChecked callback.
    React.useEffect(() => {
        if (currentArticle?.id) {
            setPendingSelection({
                articleId: currentArticle.id,
                sectionId: currentSectionId,
                columnId: currentColumnId,
                categoryId: currentCategory?.id,
                subCategoryId: currentSubCategory?.id,
            });
        }
    }, [currentArticle, currentSectionId, currentColumnId, currentCategory, currentSubCategory]);

    // Merge currentArticle (freshly fetched by selectValue) into the cache so the tree
    // can render its sections without storing anything in the cache during selectValue.
    // This mirrors how WikiArticleSelection uses currentArticle.sections directly.
    const effectiveCache = React.useMemo(() => {
        if (currentArticle?.id && !articleDetailsCache[currentArticle.id]) {
            return { ...articleDetailsCache, [currentArticle.id]: currentArticle };
        }
        return articleDetailsCache;
    }, [articleDetailsCache, currentArticle]);

    const catId = currentCategory?.id;
    const subCatId = currentSubCategory?.id;
    const articleId = currentArticle?.id;
    const preselectedPath = React.useMemo<AscsPreselectedPath>(() => ({
        categoryId: catId,
        subCategoryId: subCatId,
        articleId,
        sectionId: currentSectionId,
        columnId: currentColumnId,
    }), [catId, subCatId, articleId, currentSectionId, currentColumnId]);

    const yourChoice = React.useMemo((): React.ReactNode => {
        if (!pendingSelection) return null;
        const { categoryId, subCategoryId, articleId, sectionId, columnId } = pendingSelection;
        const parts: string[] = [];
        let typeLabel = '';
        const findCat = (id: string) => {
            for (const cat of categories ?? []) {
                if (cat.id === id) return cat;
                const sub = (cat.children ?? []).find(s => s.id === id);
                if (sub) return sub;
            }
            return undefined;
        };
        if (articleId) {
            if (categoryId) parts.push(getTranslated(findCat(categoryId)?.names, contentLanguage).text || categoryId);
            if (subCategoryId) parts.push(getTranslated(findCat(subCategoryId)?.names, contentLanguage).text || subCategoryId);
            const article = articleDetailsCache[articleId] ?? currentArticle;
            parts.push(article?.headline || articleId);
            if (sectionId) {
                const section = articleDetailsCache[articleId]?.sections?.find(s => s.id === sectionId);
                parts.push(getRidOfHtml(section?.title) || t('no title'));
                if (columnId) {
                    const col = section?.columns?.find(c => c.id === columnId);
                    parts.push(getRidOfHtml(col?.title) || t('no title'));
                    typeLabel = 'Column';
                } else {
                    typeLabel = 'Section';
                }
            } else {
                typeLabel = 'Article';
            }
        } else if (subCategoryId) {
            if (categoryId) parts.push(getTranslated(findCat(categoryId)?.names, contentLanguage).text || categoryId);
            parts.push(getTranslated(findCat(subCategoryId)?.names, contentLanguage).text || subCategoryId);
            typeLabel = 'SubCategory';
        } else if (categoryId) {
            parts.push(getTranslated(findCat(categoryId)?.names, contentLanguage).text || categoryId);
            typeLabel = 'Category';
        }
        if (parts.length === 0) return null;
        return (
            <>
                {parts.map((part, i) => (
                    <React.Fragment key={i}>
                        {i > 0 && <Icon name="chevron right" size={12} marginTop={0} style={{ transform: "translateY(2px)" }} color='@accentGreen' />}
                        {i === parts.length - 1 ? <><b>{typeLabel && <span style={{ marginRight: 4, textTransform: "uppercase" }}>{typeLabel}</span>}<Icon name="arrow right" size={12} marginTop={0} style={{ transform: "translateY(2px)", marginRight: 4 }} color='@accentGreen' />{part}</b> </> : <span>{part}</span>}

                    </React.Fragment>
                ))}
            </>
        );
    }, [pendingSelection, categories, articleDetailsCache, currentArticle, contentLanguage, t]);

    const hasExistingLink = !!props.value;
    const { /*noGroupSelect,*/ selectSection, selectColumns, maxLevel } = props;

    const sendButton = React.useMemo(() => (
        <SBContainer>
            {/* <div> */}
            {hasExistingLink && (
                <>
                    <span>{t("delete link")}</span>
                    <SwitchButton
                        initialState={deleteMode}
                        isActive={() => setDeleteMode(d => !d)}
                        activeDotColor='@mainBackground'
                        activeColor='@accentRed'
                    />
                </>
            )}

            <Button
                icon={deleteMode ? "delete" : "check"}
                tooltip={{ tooltipText: deleteMode ? t("remove link") : (pendingSelection === undefined ? t("you have to make a choice") : t("apply link")) }}
                floated='right'
                kind={deleteMode ? 'primary' : pendingSelection !== undefined ? "sendButton" : 'secondary'}
                disabled={!deleteMode && pendingSelection === undefined}
                onClick={() => {
                    if (deleteMode) {
                        props.onLinkRemove?.();
                    } else if (pendingSelection) {
                        const aid = pendingSelection.articleId;
                        if (aid) {
                            sectionColumnSelected(aid, pendingSelection.sectionId, pendingSelection.columnId);
                        } else if (pendingSelection.categoryId) {
                            props.onCategorySelected?.(pendingSelection.categoryId, pendingSelection.subCategoryId);
                        }
                    }
                }}
            />
        </SBContainer>
    ), [pendingSelection, deleteMode, sectionColumnSelected, props, t, hasExistingLink]);

    if (selectedGroupid?.length === 0) {
        return (
            <div style={{ marginTop: -1 }}>
                <GroupSelection
                    groupTypes={['WIKI']}
                    title={t('group selection')}
                    onChange={onChangeGroup}
                    value={null}
                />
            </div>
        );
    }

    if (!categories) {
        return props.showCatLoader === true
            ? <LoaderInline active infoText={t('loading categories')} />
            : <div />;
    }

    return (
        <>
            <InfoRow>
                {pendingSelection === undefined ?
                    <GroupSelection
                        groupTypes={['WIKI']}
                        onChange={onChangeGroup}
                        value={_.find(userGroups, g => g.id === selectedGroupid)}
                    />
                    :
                    <ErrorInfo error={false} warn={deleteMode}>
                        <Icon name="info" style={{ float: "left", marginRight: 5 }} marginTop={-1} />
                        {deleteMode
                            ? <span>
                                {t("link will be removed")}
                            </span>
                            :
                            <span>{t("your choice")}: {yourChoice}</span>
                        }
                    </ErrorInfo>
                }

                {sendButton}

                <Container>
                    <AscsTreeView
                        categories={categories ?? []}
                        articles={articles}
                        articleDetailsCache={effectiveCache}
                        loadingArticleIds={loadingArticleIds}
                        loadingSubcatIds={loadingSubcatIds}
                        contentLanguage={contentLanguage}
                        selectSection={selectSection}
                        selectColumns={selectColumns}
                        t={t}
                        onShowSubcats={showSubcats}
                        onLoadSubcatArticles={loadSubcatArticles}
                        onLoadSubcatWithSections={loadSubcatWithSections}
                        onShowArticle={showArticle}
                        onSectionSelected={(articleId, sectionId) => sectionColumnSelected(articleId, sectionId)}
                        onColumnSelected={(articleId, sectionId, columnId) => sectionColumnSelected(articleId, sectionId, columnId)}
                        onChecked={setPendingSelection}
                        preselectedPath={preselectedPath}
                        selectionDisabled={deleteMode}
                        maxLevel={maxLevel}
                    />
                </Container>
                <ErrorInfo error={!deleteMode && pendingSelection === undefined} warn={deleteMode}>
                    {deleteMode
                        ? <span>{t("link will be removed")}</span>
                        : pendingSelection === undefined
                            ? <span>{t("you have to make a choice")}</span>
                            : <span>{t("your choice")}: {yourChoice}</span>
                    }
                </ErrorInfo>
                {sendButton}
            </InfoRow>
        </>
    );
};

export default ArticleSectionColumnSelection;
